import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { VoicesTopicStatus } from "../../../Enums/voices";
import { VoicesTopic, VoicesTopicPayload } from "../../../Models/voicesTopics";
import { useVoicesPost } from "../../../Query/useVoicesPosts";
import {
  useDeleteVoicesTopic,
  useSaveVoicesTopic,
  useSetVoicesTopicStatus,
} from "../../../Query/useVoicesTopics";
import { ComoButton } from "../../Common/ComoButton";
import VoicesConfirmDialog from "../Common/VoicesConfirmDialog";
import { STATE_KEYS } from "../Calendar/VoicesCalendarPostChip";
import VoicesField from "../Common/VoicesField";
import { VoicesStatusTag } from "../Common/VoicesFilterParts";
import VoicesPostDialog from "../Posts/VoicesPostDialog";
import { formatPostTime } from "../Posts/VoicesPostRows";
import { STATE_TONES } from "../Posts/VoicesPostStateChip";
import VoicesTopicChips, { MAX_SCORE_PER_CRITERION } from "./VoicesTopicChips";
import VoicesTopicForm, { EMPTY_TOPIC, topicFormIsValid } from "./VoicesTopicForm";

interface Props {
  open: boolean;
  campaignId: number;
  topic: VoicesTopic | null;
  canManage: boolean;
  onCreatePost?: (topic: VoicesTopic) => void;
  onClose: () => void;
}

const textButtonSx = { color: color.gray600, textTransform: "none" };

const VoicesTopicDialog: React.FC<Props> = ({
  open,
  campaignId,
  topic,
  canManage,
  onCreatePost,
  onClose,
}) => {
  const { t } = useTranslation("voicesTopics");
  const { t: tPosts } = useTranslation("voicesPosts");
  const { colors: workspace } = useWorkspaceColors();
  const saveTopic = useSaveVoicesTopic(campaignId);
  const setStatus = useSetVoicesTopicStatus(campaignId);
  const deleteTopic = useDeleteVoicesTopic(campaignId);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<VoicesTopicPayload>(EMPTY_TOPIC);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [openPostId, setOpenPostId] = useState<number | null>(null);
  const { data: openPost } = useVoicesPost(openPostId);

  const isNew = !topic;

  useEffect(() => {
    if (!open) return;
    setEditing(isNew);
    setForm(EMPTY_TOPIC);
  }, [open, isNew]);

  const startEditing = (shown: VoicesTopic) => {
    setForm({
      title: shown.title,
      summary: shown.summary,
      sources: shown.sources.length ? shown.sources : EMPTY_TOPIC.sources,
      confidential: shown.confidential,
      notes: shown.notes,
    });
    setEditing(true);
  };

  const save = () =>
    saveTopic.mutate(
      { topicId: topic ? topic.id : null, data: form },
      { onSuccess: () => (topic ? setEditing(false) : onClose()) },
    );

  const changeStatus = (status: VoicesTopicStatus) =>
    topic && setStatus.mutate({ topicId: topic.id, status }, { onSuccess: onClose });

  const renderView = (shown: VoicesTopic) => (
    <Stack spacing={2.5}>
      <VoicesTopicChips topic={shown} showOrigin />

      <VoicesField label={t("summary")}>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {shown.summary || t("noSummary")}
        </Typography>
      </VoicesField>

      <VoicesField label={t("sources")}>
        <Stack spacing={0.5}>
          {shown.sources.map((source) => (
            <Box key={source.url} sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
              <Link
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ color: workspace.accent, fontWeight: 500 }}
              >
                {source.name}
              </Link>
              {source.date && (
                <Typography variant="caption">
                  {new Date(`${source.date}T00:00:00`).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </VoicesField>

      <VoicesField label={t("postsFromTopic")}>
        {!shown.posts.length ? (
          <Typography sx={{ fontSize: 13, color: color.gray500 }}>
            {t("noPostsFromTopic")}
          </Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, mx: -1 }}>
            {shown.posts.map((post) => (
              <Box component="li" key={post.id}>
                <ButtonBase
                  onClick={() => setOpenPostId(post.id)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: "10px",
                    textAlign: "left",
                    "&:hover": { backgroundColor: alpha(workspace.accent, 0.08) },
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: color.gray900 }}>
                    {post.target.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: color.gray500 }}>
                    {post.scheduled_at && formatPostTime(post.scheduled_at)}
                  </Typography>
                  <Box sx={{ ml: "auto" }}>
                    <VoicesStatusTag
                      label={tPosts(STATE_KEYS[post.state])}
                      tone={STATE_TONES[post.state]}
                    />
                  </Box>
                </ButtonBase>
              </Box>
            ))}
          </Box>
        )}
      </VoicesField>

      <VoicesField label={t("notes")}>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {shown.notes || t("noNotes")}
        </Typography>
      </VoicesField>

      {shown.scores.length > 0 && (
        <VoicesField label={t("scoreDetails")}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("criterion")}</TableCell>
                <TableCell align="center">{t("points")}</TableCell>
                <TableCell>{t("reason")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.scores.map((score) => (
                <TableRow key={score.criterion}>
                  <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>
                    {t(`criterion_${score.criterion}`, { defaultValue: score.criterion })}
                  </TableCell>
                  <TableCell align="center">
                    {score.points}/{MAX_SCORE_PER_CRITERION}
                  </TableCell>
                  <TableCell>{score.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </VoicesField>
      )}
    </Stack>
  );

  const renderViewActions = (shown: VoicesTopic) => {
    const dismissed = shown.status === VoicesTopicStatus.DISMISSED;
    return (
      <>
        {canManage && (
          <Button onClick={() => setConfirmDelete(true)} sx={{ ...textButtonSx, mr: "auto" }}>
            {t("delete")}
          </Button>
        )}
        {canManage &&
          (dismissed ? (
            <Button
              onClick={() => changeStatus(VoicesTopicStatus.NEW)}
              disabled={setStatus.isPending}
              sx={textButtonSx}
            >
              {t("restore")}
            </Button>
          ) : (
            <>
              <Button
                onClick={() => changeStatus(VoicesTopicStatus.DISMISSED)}
                disabled={setStatus.isPending}
                sx={textButtonSx}
              >
                {t("dismiss")}
              </Button>
              {shown.status !== VoicesTopicStatus.USED && (
                <Button
                  onClick={() => changeStatus(VoicesTopicStatus.USED)}
                  disabled={setStatus.isPending}
                  sx={textButtonSx}
                >
                  {t("markUsed")}
                </Button>
              )}
            </>
          ))}
        <Button onClick={onClose} sx={textButtonSx}>
          {t("closeDialog")}
        </Button>
        {canManage &&
          (onCreatePost && !dismissed ? (
            <Button onClick={() => startEditing(shown)} sx={textButtonSx}>
              {t("edit")}
            </Button>
          ) : (
            <ComoButton onClick={() => startEditing(shown)}>{t("edit")}</ComoButton>
          ))}
        {onCreatePost && !dismissed && (
          <ComoButton onClick={() => onCreatePost(shown)}>{t("createPost")}</ComoButton>
        )}
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: color.gray900, fontWeight: 600, fontSize: "1.1rem" }}>
        {editing ? (topic ? t("editTopic") : t("newTopic")) : topic?.title}
      </DialogTitle>
      <DialogContent dividers>
        {editing ? (
          <VoicesTopicForm value={form} onChange={setForm} />
        ) : (
          topic && renderView(topic)
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {editing ? (
          <>
            <Button
              onClick={() => (topic ? setEditing(false) : onClose())}
              sx={textButtonSx}
            >
              {t("cancel")}
            </Button>
            <ComoButton
              onClick={save}
              disabled={!topicFormIsValid(form) || saveTopic.isPending}
            >
              {t("save")}
            </ComoButton>
          </>
        ) : (
          topic && renderViewActions(topic)
        )}
      </DialogActions>

      <VoicesConfirmDialog
        open={confirmDelete}
        title={t("deleteTitle")}
        text={t("deleteConfirm", { title: topic?.title })}
        confirmLabel={t("delete")}
        pending={deleteTopic.isPending}
        onConfirm={() =>
          topic &&
          deleteTopic.mutate(topic.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              onClose();
            },
          })
        }
        onClose={() => setConfirmDelete(false)}
      />
      <VoicesPostDialog
        open={openPostId !== null && !!openPost}
        campaignId={campaignId}
        post={openPost ?? null}
        topic={null}
        onClose={() => setOpenPostId(null)}
      />
    </Dialog>
  );
};

export default VoicesTopicDialog;
